import React, { useCallback, useMemo } from 'react';
import { Checkbox } from '@perxis-auto/ui-kit/lib/components/Checkbox';
import { ColorPicker } from '@perxis-auto/ui-kit/lib/components/ColorPicker';
import { DuoColorPicker } from '@perxis-auto/ui-kit/lib/components/DuoColorPicker';
import { ModelCheckbox } from '@perxis-auto/ui-kit/lib/components/ModelCheckbox';
import { RangeSlider } from '@perxis-auto/ui-kit/lib/components/RangeSlider';
import { Select } from '@perxis-auto/ui-kit/lib/components/Select';
import { Slider } from '@perxis-auto/ui-kit/lib/components/Slider';

import defaultFormatFilterValue from '@/features/filters/helpers/formatFilterValue';
import { useAppDispatch } from '@/features/hooks';
import { rootActions } from '@/features/store';
import { ConstructorType } from '@/interfaces/constructor.interfaces';
import debounce from '@/utils/debounce';

import FilterLocation from '../FilterLocation';

import { FilterItemProps } from './FilterItem.types';

const renderFilterComponent = (
  type: ConstructorType,
  props?: Record<string, unknown>,
) => {
  switch (type) {
    case ConstructorType.FilterCheckboxGroup: {
      return <Checkbox.Group {...props} />;
    }
    case ConstructorType.FilterModelCheckboxGroup: {
      return (
        <ModelCheckbox.Group
          type="primary"
          layout="compact"
          checkboxLayout="check-icon"
          gutter={[8, 8]}
          xs={24}
          sm={12}
          md={24}
          {...props}
        />
      );
    }
    case ConstructorType.FilterLocation: {
      return <FilterLocation {...props} />;
    }
    case ConstructorType.FilterColorPicker: {
      return <ColorPicker {...props} />;
    }
    case ConstructorType.FilterDuoColorPicker: {
      return <DuoColorPicker {...props} />;
    }
    case ConstructorType.FilterSelect: {
      return <Select allowClear {...props} />;
    }
    case ConstructorType.FilterMultipleSelect: {
      return <Select mode="multiple" {...props} />;
    }
    case ConstructorType.FilterSlider: {
      return <Slider {...props} />;
    }
    case ConstructorType.FilterRangeSlider: {
      return <RangeSlider {...props} />;
    }
    default:
      return null;
  }
};

function FilterItem({
  item: filterItem,
  formatFilterValue = defaultFormatFilterValue,
  onChange,
}: FilterItemProps) {
  const dispatch = useAppDispatch();

  const { key, type, props, value } = filterItem;

  const handleChange = useCallback(
    (value: any) => {
      const formattedValue = formatFilterValue(value);
      dispatch(rootActions.choice.setFilters({ [`${key}`]: formattedValue }));
      onChange?.(key);
    },
    [dispatch, formatFilterValue, key, onChange],
  );

  const preparedProps = useMemo(() => {
    return {
      ...props,
      value,
      onChange: debounce(handleChange),
    };
  }, [props, value, handleChange]);

  if (!filterItem) {
    return null;
  }

  return renderFilterComponent(type, preparedProps);
}

export default FilterItem;
